import type { ApiResponse, PaginatedResponse } from '@/services/types'
import type { UserRole } from '@/types/auth'
import type { Order } from '@/types/order'
import type { SubscriptionStatus, BillingCycle } from '@/types/subscription'
import type { LimitType, LimitPeriod } from '@/types/plan'
import type { OrganizationType } from '@/types/organization'

// =================== User Profile ===================

export type ThemePreference = 'light' | 'dark' | 'system'
export type FontSizePreference = 'normal' | 'large'
export type ExtendedUserAccountStatus = 'normal' | 'frozen' | 'pending_deletion'

export interface UserProfile {
  id: string
  mobile: string
  email?: string
  avatarUrl?: string
  displayName: string
  realName?: string
  /** 所在地区：个人体验空间的单位档案由此回填 */
  region?: string
  wechatOpenId?: string
  wechatBound: boolean
  /** 是否已设置登录密码（短信注册用户为 false，需引导通过手机验证码设置） */
  hasPassword: boolean
  organizationId?: string
  organizationName?: string
  organizationType?: OrganizationType
  role: UserRole
  status: ExtendedUserAccountStatus
  theme: ThemePreference
  fontSize: FontSizePreference
  createdAt: Date
  lastLoginAt: Date
}

export interface UpdateProfileDto {
  displayName?: string
  realName?: string
  email?: string
  region?: string
  // 修复项：新增 theme 和 fontSize 字段，支持通过 updateUserProfile 同步用户偏好到后端。
  theme?: ThemePreference
  fontSize?: FontSizePreference
}

// =================== Organization Membership ===================

export type OrgApplicationStatus = 'pending' | 'approved' | 'rejected' | 'withdrawn' | 'leave_pending' | 'leave_approved' | 'leave_rejected'

export interface OrgMembershipApplication {
  id: string
  applicantId: string
  applicantName: string
  targetOrgId: string
  targetOrgName: string
  targetOrgType: OrganizationType
  reason: string
  status: OrgApplicationStatus
  reviewerId?: string
  reviewerName?: string
  reviewedAt?: Date
  rejectReason?: string
  createdAt: Date
  updatedAt: Date
}

export interface OrgMembership {
  orgId: string
  orgName: string
  orgType: OrganizationType
  joinedAt: Date
  roleInOrg: UserRole
  isPrimary: boolean
}

export interface ApplyForOrgDto {
  targetOrgId: string
  reason: string
}

export interface LeaveOrgDto {
  reason?: string
}

// =================== Subscription View ===================

export interface UserSubscriptionView {
  subscriptionId: string
  planId: string
  planName: string
  planType: 'free' | 'pro'
  status: SubscriptionStatus
  billingCycle: BillingCycle
  startedAt: Date
  expiresAt: Date
  autoRenew: boolean
  limits: PlanLimitUsage[]
}

export interface PlanLimitUsage {
  limitType: LimitType
  limitLabel: string
  total: number
  used: number
  remaining: number
  period: LimitPeriod
}

// =================== Order with Invoice ===================

export interface OrderWithInvoice extends Order {
  invoiceStatus?: 'not_applied' | 'applied' | 'issued'
  invoice?: InvoiceInfo
}

export interface InvoiceInfo {
  title: string
  taxNumber: string
  email: string
  issuedAt?: Date
}

export interface ApplyInvoiceDto {
  orderId: string
  title: string
  taxNumber: string
  email: string
}

// =================== Notification Preference ===================
// P8-fix: 旧版通知偏好类型（NotificationType/NotificationChannel/NotificationPreference/
// UpdateChannelDto/BatchUpdatePreferenceDto）已随旧接口一并删除，
// 通知偏好统一使用 @/types/notifications 的 MessagePreference。

// =================== Login Device ===================

export type DeviceType = 'web' | 'miniapp' | 'app'

export interface LoginDevice {
  id: string
  userId: string
  deviceName: string
  deviceType: DeviceType
  browser?: string
  os?: string
  ipAddress: string
  location?: string
  loginAt: Date
  lastActiveAt: Date
  isCurrentDevice: boolean
  sessionStatus: 'active' | 'terminated'
}

// =================== Feedback ===================

export type FeedbackType = 'feature' | 'usage' | 'account' | 'complaint'

/**
 * P0-fix: 用户侧反馈类型到管理侧 category 的映射。
 * 用户端提交时仍使用业务友好的四字类型，后端统一转换为管理侧 category。
 */
export const FeedbackTypeToCategory: Record<FeedbackType, string> = {
  feature: 'feature_request',
  usage: 'consultation',
  account: 'bug_report',
  complaint: 'other',
}

/**
 * P0-fix: 管理侧 category 回显为中文标签。
 */
export const FeedbackCategoryLabels: Record<string, string> = {
  feature_request: '功能建议',
  consultation: '使用咨询',
  bug_report: 'Bug 报告',
  other: '其他',
}

/**
 * P0-fix: 统一反馈状态。
 * 后端管理侧使用 pending/processing/replied/closed，用户端列表展示时映射为 submitted/processing/resolved/closed。
 */
export type FeedbackStatus = 'submitted' | 'processing' | 'resolved' | 'closed'

/**
 * 管理侧状态到用户侧状态的映射。
 */
export const AdminStatusToUserStatus: Record<string, FeedbackStatus> = {
  pending: 'submitted',
  processing: 'processing',
  replied: 'resolved',
  closed: 'closed',
}

/**
 * 用户侧状态展示标签。
 */
export const FeedbackStatusLabels: Record<FeedbackStatus, string> = {
  submitted: '已提交',
  processing: '处理中',
  resolved: '已回复',
  closed: '已关闭',
}

export interface FeedbackTicket {
  id: string
  feedbackNo?: string
  userId: string
  type: string
  typeLabel: string
  subject: string
  description: string
  screenshots?: string[]
  status: FeedbackStatus
  createdAt: Date
  updatedAt: Date
  resolvedAt?: Date
  repliedAt?: Date
  closedAt?: Date
  reply?: string
  handlerName?: string
  closeReason?: string
}

export interface CreateFeedbackDto {
  type: FeedbackType
  subject: string
  description: string
  screenshots?: string[]
}

export interface FaqItem {
  id: string
  category: string
  question: string
  answer: string
  order: number
}

// =================== Account Deletion ===================

export type DeletionStatus = 'pending' | 'cooling_down' | 'completed' | 'cancelled'

export interface AccountDeletionRequest {
  id: string
  userId: string
  reason?: string
  status: DeletionStatus
  requestedAt: Date
  coolingDownEndsAt?: Date
  completedAt?: Date
  cancelledAt?: Date
}

/** 注销前置条件检查结果。activeRequest 携带进行中的申请，供刷新后恢复「处理中」面板 */
export interface DeletionCheckResult {
  canDelete: boolean
  reasons: string[]
  activeRequest?: AccountDeletionRequest | null
}

/** 提交注销申请：code 为短信验证码（二次身份验证，注销不可逆） */
export interface RequestDeletionDto {
  reason?: string
  code: string
}

/** 撤销注销申请：同样需要短信验证码确认身份 */
export interface CancelDeletionDto {
  code: string
}


// =================== Password ===================

export interface ChangePasswordDto {
  oldPassword: string
  newPassword: string
}

export interface ResetPasswordByCodeDto {
  mobile: string
  code: string
  newPassword: string
}

/**
 * 后端下发的密码规则（GET /api/user/settings/password-policy）。
 *
 * 规则与文案都以服务端 security_policy 为单源：前端不再复写正则，placeholder 直接用 ruleText。
 */
export interface PasswordPolicy {
  minLength: number
  maxLength: number
  requireUppercase: boolean
  requireLowercase: boolean
  requireDigit: boolean
  requireSpecialChar: boolean
  ruleText: string
}

// =================== Wechat Bind ===================

export interface WechatBindStatus {
  bound: boolean
  nickname?: string
}

// =================== API Return Types ===================

export type UserProfileResponse = ApiResponse<UserProfile>
export type FeedbackListResponse = PaginatedResponse<FeedbackTicket>
